<template>
  <ValidationObserver ref="obs">
    <v-form autocomplete="off" class="fill-height">
      <v-card class="fill-height d-flex flex-column">
        <v-toolbar dark color="primary" flat style="max-height: 64px;">
          <v-toolbar-title>
            {{ formTitle }} '{{ value.ednNumber }}' for Unit
            <router-link class="accent--text text--lighten-1" :to="{ name: 'unit', params: { unitId: value.unitId } }">{{ value.unitNumber }} </router-link>
          </v-toolbar-title>
          <v-subheader class="subtitle" v-if="!value.isStale"> Submitted By: {{ createdBy }} </v-subheader>
          <slot name="header" v-if="!value.isStale"></slot>
        </v-toolbar>

        <v-card-text class="flex-grow-1">
          <stale-data-alert :is-stale="value.isStale || false" message="This defect notice has been updated by another user. Please refresh to see updated data." @refresh="refreshPage"></stale-data-alert>

          <v-container v-if="!value.isStale">
            <v-container>
              <v-row v-if="value.sourceSystem === 'GateKeeper'">
                <v-col>
                  <v-text-field v-model="incidentNumber" label="Incident/Reference #" name="incidentNumber" hide-details></v-text-field>
                </v-col>
                <v-col>
                  <v-text-field :value="value.receivedTotal" label="Defects Received" name="receivedTotal" disabled hide-details></v-text-field>
                </v-col>
                <v-col>
                  <v-text-field :value="value.completedOnReceipt" label="Defects Completed on Receipt" name="completedOnReceipt" disabled hide-details></v-text-field>
                </v-col>
                <v-col>
                  <v-text-field :value="scheduledDefectsOutstanding" label="Defects Remaining" name="scheduledDefectsOutstanding" disabled hide-details></v-text-field>
                </v-col>
              </v-row>
              <v-row v-else>
                <v-col>
                  <v-text-field v-model="incidentNumber" label="Incident/Reference #" name="incidentNumber" hide-details></v-text-field>
                </v-col>
                <v-col>
                  <v-text-field :value="value.receivedTotal" label="Defects Received" name="receivedTotal" disabled hide-details></v-text-field>
                </v-col>
                <v-col>
                  <v-text-field :value="pendingTotal" label="Defects Pending" name="pendingTotal" disabled hide-details></v-text-field>
                </v-col>
                <v-col>
                  <v-text-field :value="scheduledTotal" label="Defects Scheduled" name="scheduledTotal" disabled hide-details></v-text-field>
                </v-col>
                <v-col>
                  <v-text-field :value="completedTotal" label="Defects Completed" name="completedTotal" disabled hide-details></v-text-field>
                </v-col>
              </v-row>
            </v-container>
            <v-container v-if="value.completedOnReceipt > 0">
              <v-row>
                <v-col>
                  <v-row v-if="value.precompletedDefects.length <= value.completedOnReceipt">
                    <v-col>
                      <h3 class="title">Defects Completed on Receipt</h3>
                      <p class="caption" v-if="value.precompletedDefects.length === 0">(no details entered yet)</p>
                    </v-col>
                    <v-col class="text-right">
                      <v-btn color="primary" @click="addComplete" :disabled="!allowMoreDetail">Add Details </v-btn>
                      <div class="caption" v-if="allowMoreDetail">(Click once for each defect)</div>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-if="value.precompletedDefects && value.precompletedDefects.length > 0" v-for="precompletedDefect in value.precompletedDefects" :key="precompletedDefect.id">
                <v-row>
                  <v-col cols="1">
                    <ValidationProvider name="Line Number" rules="required|uniquelinenumber|max_value:30|min_value:1" v-slot="{ errors }">
                      <v-select v-model.number="precompletedDefect.lineNumber" label="#" :items="value.sourceSystem === 'OnRoad' ? onRoadNumberData : numberData" :error-messages="errors"></v-select>
                    </ValidationProvider>
                  </v-col>
                  <v-col cols="3">
                    <ValidationProvider name="Description // Repair Details" rules="required|max:4000" v-slot="{ errors }">
                      <v-textarea v-model="precompletedDefect.description" :error-messages="errors" label="Describe the defect // Repair details" auto-grow rows="1"></v-textarea>
                    </ValidationProvider>
                  </v-col>
                  <v-col cols="3">
                    <ValidationProvider name="Completed By" rules="required|max:200" v-slot="{ errors }">
                      <v-textarea v-model="precompletedDefect.completedBy" :error-messages="errors" label="Who completed this defect?" auto-grow rows="1"></v-textarea>
                    </ValidationProvider>
                  </v-col>
                  <v-col cols="2">
                    <ValidationProvider name="Category" rules="required" v-slot="{ errors }">
                      <v-select label="Category" v-model="precompletedDefect.defectCategoryId" item-value="id" item-text="name" :items="categories" :error-messages="errors"></v-select>
                    </ValidationProvider>
                  </v-col>
                  <v-col cols="1">
                    <ValidationProvider name="Hours worked" rules="required|min_value:0" v-slot="{ errors }">
                      <decimal label="Time Taken" placeholder="e.g. 1.5" v-model.number="precompletedDefect.hoursWorked" :error-messages="errors" :precision="2" :pad="false">
                        <template v-slot:append
                          >hrs</template
                        >
                      </decimal>
                    </ValidationProvider>
                  </v-col>
                  <v-col cols="1">
                    <ValidationProvider name="Hrs / Kms" rules="required" v-slot="{ errors }">
                      <v-select label="Meter Type" :items="intervalTypes" v-model="precompletedDefect.completionMeterType" :error-messages="errors" required @change="reValidate('meterReading', item.completionMeterReading)"></v-select>
                    </ValidationProvider>
                  </v-col>
                  <v-col cols="1">
                    <ValidationProvider name="Meter Reading" :rules="'required|in-range-completeddefect:' + precompletedDefect.completionMeterType" v-slot="{ errors }">
                      <decimal placeholder="e.g. 220" name="meterReading" label="Reading" v-model="precompletedDefect.completionMeterReading" :precision="1" :error-messages="errors"></decimal>
                    </ValidationProvider>
                  </v-col>
                </v-row>
                <v-row>
                  <v-checkbox class="mt-n4 mb-n8" v-if="value.precompletedDefects && value.precompletedDefects.length > 0 && userIsFleetManager" v-model="disableMeterValidation" @change="$validator.validate()">
                    <template v-slot:label
                      >Disable Meter Validation</template
                    >
                  </v-checkbox>
                </v-row>
              </v-row>
            </v-container>
            <v-container v-if="completedDefects.length > 0">
              <v-row>
                <v-col>
                  <h3 class="title">Completed Defects</h3>
                </v-col>
              </v-row>
              <v-row v-if="completedDefects && completedDefects.length > 0" v-for="completedDefect in completedDefects" :key="completedDefect.id">
                <v-row>
                  <v-col :cols="completedDefect.photoBlobUri ? 9 : 12">
                    <v-row>
                      <v-col cols="2">
                        <ValidationProvider name="Line Number" rules="required|uniquelinenumber|max_value:30|min_value:1" v-slot="{ errors }">
                          <v-select v-model.number="completedDefect.lineNumber" label="#" :items="value.sourceSystem === 'OnRoad' ? onRoadNumberData : numberData" :error-messages="errors"></v-select>
                        </ValidationProvider>
                      </v-col>
                      <v-col cols="5">
                        <ValidationProvider name="Description" rules="required|max:4000" v-slot="{ errors }">
                          <v-textarea v-model="completedDefect.description" :error-messages="errors" label="Describe the defect" auto-grow rows="1"></v-textarea>
                        </ValidationProvider>
                      </v-col>
                      <v-col cols="5">
                        <ValidationProvider name="Repair Details" rules="required|max:4000" v-slot="{ errors }">
                          <v-textarea v-model="completedDefect.completionNotes" :error-messages="errors" label="Describe the repair details" auto-grow rows="1"></v-textarea>
                        </ValidationProvider>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="3">
                        <ValidationProvider name="Completed By" rules="required|max:200" v-slot="{ errors }">
                          <v-textarea v-model="completedDefect.completedBy" :error-messages="errors" label="Who completed this defect?" auto-grow rows="1"></v-textarea>
                        </ValidationProvider>
                      </v-col>
                      <v-col cols="3">
                        <ValidationProvider name="Category" rules="required" v-slot="{ errors }">
                          <v-select label="Category" v-model="completedDefect.defectCategoryId" item-value="id" item-text="name" :items="categories" :error-messages="errors"></v-select>
                        </ValidationProvider>
                      </v-col>
                      <v-col cols="2">
                        <ValidationProvider name="Hours worked" rules="required|min_value:0" v-slot="{ errors }">
                          <decimal label="Time Taken" placeholder="e.g. 1.5" v-model.number="completedDefect.hoursWorked" :error-messages="errors" :precision="2" :pad="false">
                            <template v-slot:append
                              >hrs</template
                            >
                          </decimal>
                        </ValidationProvider>
                      </v-col>
                      <v-col cols="2">
                        <ValidationProvider name="Hrs / Kms" rules="required" v-slot="{ errors }">
                          <v-select label="Meter Type" :items="intervalTypes" v-model="completedDefect.completionMeterType" :error-messages="errors" required @change="reValidate('meterReading', item.completionMeterReading)"></v-select>
                        </ValidationProvider>
                      </v-col>
                      <v-col cols="2">
                        <ValidationProvider name="Meter Reading" :rules="'required|in-range-completeddefect:' + completedDefect.completionMeterType" v-slot="{ errors }">
                          <decimal placeholder="e.g. 220" name="meterReading" label="Reading" v-model="completedDefect.completionMeterReading" :precision="1" :error-messages="errors"></decimal>
                        </ValidationProvider>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-spacer></v-spacer>
                      <v-btn v-if="!otherLogVisible" color="tertiary" class="mb-2 mr-5" @click="toggleChildLog(completedDefect)">
                        Logs
                        <v-icon small v-if="showChildLog">chevron_left</v-icon>
                        <v-icon small v-else>chevron_right</v-icon>
                      </v-btn>
                    </v-row>
                  </v-col>
                  <v-col v-if="completedDefect.photoBlobUri" cols="3">
                    <v-img :src="completedDefect.photoBlobUri" max-width="300" max-height="240" contain class="rounded elevation-2 md-auto" style="cursor: pointer" @click="openPhotoModal(completedDefect.photoBlobUri, completedDefect.id)">
                      <template v-slot:placeholder>
                        <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                      </template>
                    </v-img>
                  </v-col>
                  <v-divider inset></v-divider>
                </v-row>
              </v-row>
              <v-checkbox class="mb-n8" v-if="completedDefects && completedDefects.length > 0 && userIsFleetManager" v-model="disableMeterValidation" @change="$validator.validate()">
                <template v-slot:label
                  >Disable Meter Validation</template
                >
              </v-checkbox>
            </v-container>
            <v-container v-if="(!editing && existingUnitVors.length > 0) || scheduledDefects.length > 0">
              <v-row v-if="!editing && existingUnitVors.length > 0">
                <v-col>
                  <v-alert dense border="left" type="warning" class="mb-0">A VOR already exists for this Unit </v-alert>
                  <v-data-table :items="existingUnitVors" :headers="vorHeaders" :loading="$wait.is(['loading vor active'])" hide-default-footer class="vor">
                    <v-progress-linear v-slot:progress color="blue" indeterminate></v-progress-linear>
                    <template v-slot:item="items">
                      <tr>
                        <td>{{ items.item.startDate | formatDate }}</td>
                        <td>{{ items.item.endDate | formatDate }}</td>
                        <td>{{ items.item.reason }}</td>
                      </tr>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
              <v-row v-if="scheduledDefects.length > 0">
                <v-col class="title">{{ scheduledRowsTitle }}</v-col>
              </v-row>
              <template v-for="(defect, index) of scheduledDefects">
                <schedule-defect-row
                  :index="index"
                  :defect="defect"
                  :sourceSystem="value.sourceSystem"
                  :allDefects="scheduledDefects"
                  :sources="sources"
                  :categories="categories"
                  :ratings="ratings"
                  :key="index"
                  :editing="editing"
                  :canEditExpectedDate="canEditExpectedDate"
                  :reasons="reasons"
                  @ratingChanged="ratingChanged"
                ></schedule-defect-row>
              </template>
            </v-container>
            <v-container v-if="requiresVor || hasVor">
              <v-row>
                <v-col>
                  <v-alert dense border="left" type="warning" class="mb-0">Urgent and MOR Ratings require VOR</v-alert>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <vor-form ref="vor" :value="vor" :embedded="true" :disabled="editing && hasVor" @edit="hasManuallyEditedVor = true"></vor-form>
                </v-col>
              </v-row>
            </v-container>
            <v-container v-if="value.garageLocationId != value.unitGarageLocationId">
              <v-row class="my-n4">
                <v-col class="my-n4">
                  <v-checkbox v-model="transferToHome" hide-details label="Transfer this EDN to the Unit's home garage?" class="mt-0"></v-checkbox>
                </v-col>
              </v-row>
            </v-container>
          </v-container>
        </v-card-text>

        <v-card-actions v-if="!value.isStale">
          <v-spacer></v-spacer>
          <slot name="actions"></slot>
          <v-btn color="red white--text" @click="deleteEdn" v-if="userIsFleetManager">Delete EDN </v-btn>
          <v-btn color="primary" @click="submitForm">{{ submitButtonText }} </v-btn>
        </v-card-actions>
      </v-card>
      <photo-modal ref="photoModal" :photo-url="photoModalData.photoUrl" :defect-id="photoModalData.defectId" @photo-deleted="handlePhotoDeleted" />
    </v-form>
  </ValidationObserver>
</template>

<script>
import moment from 'moment';
import Decimal from '@/components/Decimal.vue';
import FormValidation from '@/components/FormValidation.vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { DefectSourcesStore } from '@/stores/DefectSourcesStore';
import { DefectRatingsStore } from '@/stores/DefectRatingsStore';
import VorForm from '@/components/Units/Details/VehicleOffRoad/Form.vue';
import ScheduleDefectRow from './Step4ScheduleDefectRow.vue';
import { Api } from '@/plugins/api';
import { Event } from '@/plugins/bus';
import VehicleOffRoad from '@/components/Units/VehicleOffRoadClient.vue';
import MetricToleranceCheck from '@/components/MetricToleranceCheck';
import { IntervalType } from '@/models/enums';
import { DateFormat } from '@/plugins/date-filter';
import DefectHelper from '@/lib/DefectHelper';
import StaleDataAlert from '@/components/DefectNotices/StaleDataAlert.vue';
import PhotoModal from '@/components/PhotoModal.vue';
import DefectsClient from '@/components/DefectNotices/DefectNoticesClient.vue';
import { GarageLocationStore } from '@/stores/GarageLocationStore';

export default {
  components: { PhotoModal, ScheduleDefectRow, ValidationObserver, ValidationProvider, VorForm, Decimal, StaleDataAlert },
  props: {
    value: {
      required: true
    }
  },
  mixins: [DefectsClient, FormValidation, VehicleOffRoad, MetricToleranceCheck],
  computed: {
    allowMoreDetail() {
      return this.value.precompletedDefects.length < this.value.completedOnReceipt;
    },
    scheduledDefectsOutstanding() {
      return this.editing ? this.value.scheduledDefectsOutstanding : this.value.receivedTotal - this.value.completedOnReceipt;
    },
    formTitle() {
      return !this.editing ? 'Schedule EDN' : 'Update EDN';
    },
    submitButtonText() {
      if (!this.editing) {
        return `Schedule Defect${this.scheduledDefects.length > 1 ? 's' : ''}`;
      } else {
        return 'Update Defect Notice';
      }
    },
    scheduledRowsTitle() {
      return this.editing ? 'Scheduled Defects' : 'Defects to Schedule';
    },
    hasVor() {
      return this.vor && !!this.vor.id;
    },
    combinedDefectLineNumbers() {
      let scheduled = this.scheduledDefects;
      let precompleted = this.value.precompletedDefects || [];

      let combined = scheduled.map(x => x.lineNumber);
      for (let x of precompleted) {
        combined.push(x.lineNumber);
      }
      return combined;
    },
    completedTotal() {
      return this.value.defects ? this.value.defects.filter(d => d.status === 'Completed').length : 0;
    },
    scheduledTotal() {
      return this.value.defects ? this.value.defects.filter(d => d.status === 'Scheduled').length : 0;
    },
    pendingTotal() {
      return this.value.defects ? this.value.defects.length - this.completedTotal - this.scheduledTotal : 0;
    },
    canEditExpectedDate() {
      if (!this.editing || this.userIsFleetManager) {
        return true;
      }

      return this.userIsWorkshopManagerInGarage(this.value.garageLocationId, GarageLocationStore.data.userGarages);
    }
  },
  async mounted() {
    const isInRange = async (value, { interval }) => {
      if (this.disableMeterValidation) {
        return true;
      }
      const intervalType = interval == 'Hours' ? IntervalType.Hours : IntervalType.Kilometres;
      // Assume all are completed on the same day, the date the EDN was received.
      return await this.checkMetricIsInRange(this.value.unitId, intervalType, value, this.value.receivedDate);
    };

    this.$validator.extend('in-range-completeddefect', {
      validate: isInRange,
      getMessage: (field, params, data) => data.message,
      paramNames: ['interval']
    });

    this.showCalendar = false;
    this.showLog = false;
    this.showChildLog = false;
  },
  data() {
    return {
      completedDefects: [],
      scheduledDefects: [],
      submitted: false,
      sources: null,
      categories: null,
      ratings: null,
      editing: false,
      loading: true,
      incidentNumber: '',
      createdBy: '',
      initiallyRequiresVor: false,
      existingUnitVors: [],
      hasManuallyEditedVor: false,
      vor: {},
      transferToHome: false,
      disableMeterValidation: false,
      requiresVor: false,
      numberData: [1, 2, 3, 4, 5, 6],
      onRoadNumberData: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30],
      intervalTypes: ['Hours', 'Kilometres'],
      vorHeaders: [{ text: 'Start Date', value: 'startDate', sortable: true }, { text: 'End Date', value: 'endDate', sortable: true }, { text: 'Reason', value: 'reason', sortable: true }],
      showLog: false,
      showChildLog: false,
      otherLogVisible: false,
      photoModalData: {
        photoUrl: '',
        defectId: null
      },
      reasons: null
    };
  },

  async created() {
    // This is scoped to the 'this' and child components.
    // It's used on both Precompleted Defects (in this component)
    // and Scheduled Defects (in Step4ScheduledDefectRow)
    // to ensure uniqueness across both precompleted and scheduled defects.
    this.$validator.extend('uniquelinenumber', {
      validate: this.isLineNumberDuplicate,
      getMessage: field => 'Duplicate'
    });
    this.$bus.$on(Event.ParentLogVisibilityChanged, this.onParentLogVisibilityChanged);
    this.$bus.$on(Event.CloseAllLogs, this.onCloseAllLogs);
  },
  destroyed() {
    this.$bus.$off(Event.ParentLogVisibilityChanged, this.onParentLogVisibilityChanged);
    this.$bus.$off(Event.CloseAllLogs, this.onCloseAllLogs);
    this.showLog = false;
    this.showChildLog = false;
  },
  methods: {
    async onParentLogVisibilityChanged(event) {
      // toggle my own defect visibility based on my parent's visibility
      this.otherLogVisible = event.visible;
    },
    async onCloseAllLogs() {
      this.showChildLog = false;
      this.otherLogVisible = false;
    },
    isLineNumberDuplicate(value) {
      const itemsInRange = this.combinedDefectLineNumbers.filter(x => x == value);
      return itemsInRange.length < 2;
    },
    reValidate(formInput, modelField) {
      if (modelField == null || modelField == undefined || modelField == '') {
        return;
      }
      this.$refs.obs.validate(formInput, modelField);
    },
    async init(isEditMode) {
      this.resetValidation();
      this.editing = isEditMode || false;

      let val = this.value;

      this.sources = await DefectSourcesStore.methods.getData();
      this.categories = await this.getUnitDefectCategories(val.unitId);
      this.ratings = await DefectRatingsStore.methods.getData();
      this.incidentNumber = this.value.incidentNumber;
      this.createdBy = this.value.createdBy;
      this.reasons = await this.getScheduledReasons();
      this.submitted = false;
      const alreadyScheduled = val.scheduled || [];
      this.scheduledDefects = [];
      for (const scheduled of alreadyScheduled) {
        this.scheduledDefects.push(scheduled);
      }

      this.completedDefects = val.completedDefects || [];
      const numberOfScheduleToCreate = val.receivedTotal - val.completedOnReceipt - alreadyScheduled.length - this.completedDefects.length;
      for (let i = 0; i < numberOfScheduleToCreate; i++) {
        this.scheduledDefects.push({
          id: this.$uuid.v1(),
          sourceId: val.source.id,
          receivedDate: val.receivedDate
        });
      }

      this.existingUnitVors = [];
      if (!this.editing) {
        this.existingUnitVors = await this.getVehicleOffRoadActive(val.unitId);
      }

      this.vor = val.vehicleOffRoad || {};
      if (val.vehicleOffRoad) {
        this.hasManuallyEditedVor = true;
      }
      this.value.precompletedDefects = this.value.precompletedDefects || [];
      this.value.precompletedDefects = this.value.precompletedDefects.filter(x => !x.newrow) || [];
      this.ratingChanged();
    },
    toggleChildLog(defect) {
      this.showChildLog = !this.showChildLog;
      // let the parent know to show my log
      let event = { visible: this.showChildLog, defectId: defect.id, workSessions: defect.workSessions, assignees: defect.assignees, historicalAcceptedJobs: defect.historicalAcceptedJobs, defectNumber: defect.lineNumber };
      this.$bus.$emit(Event.ChildLogVisibilityChanged, event);
    },
    async submitForm() {
      this.submitted = true;
      const result = await this.$refs.obs.validate();
      const vorResult = this.$refs.vor ? await this.$refs.vor.checkValidation() : true;
      if (!result || !vorResult) {
        return;
      }

      for (let defect of this.scheduledDefects) {
        if (!defect.scheduledDate) {
          defect.scheduledDate = defect.defaultScheduledDate;
        }
      }

      try {
        this.loading = true;
        if (!this.editing) {
          await Api.put(`defect-notices/${this.value.id}/schedule`, {
            scheduledDefects: this.scheduledDefects,
            precompletedDefects: this.value.precompletedDefects,
            isVor: this.requiresVor,
            vor: this.vor,
            incidentNumber: this.incidentNumber,
            transferToHomeGarage: this.transferToHome
          });
        } else {
          await Api.put(`defect-notices/${this.value.id}/update`, {
            scheduledDefects: this.scheduledDefects,
            precompletedDefects: this.value.precompletedDefects,
            completedDefects: this.completedDefects,
            isVor: this.requiresVor,
            vor: this.vor,
            incidentNumber: this.incidentNumber,
            transferToHomeGarage: this.transferToHome
          });
        }
        this.$bus.$emit(Event.DefectSchedulesUpdated, this.value.id, { ...this.scheduledDefects });
        if (!this.editing) {
          this.$toast.success(`${this.scheduledDefects.length} defect${this.scheduledDefects.length > 1 ? 's' : ''} scheduled`);
        }
        this.$toast.success('Defect Notice updated');
      } catch (err) {
        if (err && err.response && err.response.data) {
          this.$toast.error(err.response.data);
        } else {
          this.$toast.error('Could not ' + (this.editing ? 'update' : 'schedule') + ' defects');
        }
      } finally {
        this.loading = false;
      }
    },
    async getUnitDefectCategories(unitId) {
      try {
        const unitfilter = unitId == undefined ? '' : `?unitId=${unitId}`;
        const response = await Api.get(`defect-categories${unitfilter}`);
        return response.data;
      } catch (error) {
        this.$toast.error('There was an error getting defect categories');
        return null;
      }
    },
    resetValidation() {
      this.$nextTick(async () => {
        if (this.$refs.vor) {
          await this.$refs.vor.resetValidation();
        }
        this.$refs.obs.reset();
      });
    },
    addComplete() {
      if (!this.value.precompletedDefects) {
        this.value.precompletedDefects = [];
      }

      this.value.precompletedDefects.push({
        id: this.$uuid.v1(),
        defectNoticeId: this.id,
        newrow: true,
        completionMeterType: 'Hours'
      });
    },
    ratingChanged(newold) {
      const oldRatingRequireVor = newold && newold.oldRatingId ? this.doesRatingIdRequireVor(newold.oldRatingId) : false;

      if (!this.scheduledDefects || (this.editing && oldRatingRequireVor)) {
        this.requiresVor = false;
      }
      let vor = _.find(this.scheduledDefects, x => {
        return this.doesRatingIdRequireVor(x.ratingId);
      });
      this.requiresVor = !!vor;
    },
    doesRatingIdRequireVor(ratingId) {
      if (!this.ratings) {
        return false;
      }
      let rating = _.find(this.ratings, r => r.id == ratingId);
      if (!rating) {
        return false;
      }
      return rating.requiresOffRoad;
    },
    deleteEdn() {
      this.$confirmCancel.show(`Delete Defect Notice?`, `<p>This will permanently delete this Defect Notice record</p>`, 'Delete it');
      var confirmHandler = confirmed => {
        this.$confirmCancel.$off('confirm-cancel', confirmHandler);
        if (!confirmed) {
          return;
        }

        this.$emit('delete-edn', { id: this.value.id, source: 'schedule' });
      };
      this.$confirmCancel.$on('confirm-cancel', confirmHandler);
    },
    setVorValues() {
      if (!this.requiresVor || this.hasManuallyEditedVor) {
        return;
      }

      var latestDate = null;

      for (let scheduled of this.scheduledDefects) {
        if (!scheduled.ratingId) {
          continue;
        }
        let rating = _.find(this.ratings, r => r.id == scheduled.ratingId);
        if (!rating || !rating.requiresOffRoad) {
          continue;
        }
        if (!scheduled.scheduledDate && !scheduled.categoryId) {
          continue;
        }

        var date = scheduled.scheduledDate ? moment(scheduled.scheduledDate) : DefectHelper.defaultDateFor(scheduled.categoryId, this.categories, new Date(scheduled.receivedDate));

        if (!latestDate || date.valueOf() > latestDate.valueOf()) {
          latestDate = date;
        }
      }

      if (latestDate) {
        this.$refs.vor.setEndDate(moment(latestDate).format(DateFormat.Canonical));
      }

      this.$refs.vor.setStartDate(moment(this.value.receivedDate).format(DateFormat.Canonical));
    },
    async refreshPage() {
      try {
        // Emit an event to the parent to refresh the defect page
        this.$emit('refresh-page');

        // Close the modal
        this.$emit('close-modal');
      } catch (error) {
        this.$toast.error('Failed to refresh page');
      }
    },
    openPhotoModal(photoUrl, defectId) {
      this.photoModalData.photoUrl = photoUrl;
      this.photoModalData.defectId = defectId;
      this.$refs.photoModal.showModal();
    },
    handlePhotoDeleted() {
      // Search for the defect in all possible arrays
      let defect =
        this.scheduledDefects.find(x => x.id === this.photoModalData.defectId) ||
        this.completedDefects.find(x => x.id === this.photoModalData.defectId) ||
        (this.value.precompletedDefects && this.value.precompletedDefects.find(x => x.id === this.photoModalData.defectId));

      if (defect) {
        defect.photoBlobUri = '';
      }
      this.photoModalData.photoUrl = '';
      this.$refs.photoModal.close();
    }
  },
  watch: {
    scheduledDefects: {
      handler(val) {
        //stop race conditions on flags being set
        setTimeout(() => this.setVorValues(), 1);
      },
      deep: true
    }
  }
};
</script>

<style lang="scss" scoped>
.vor {
  tbody {
    tr {
      td:first-child,
      td:nth-child(2) {
        width: 1px;
        white-space: nowrap;
      }
    }
  }
}

.defects-list {
  tbody {
    tr {
      td {
        padding: 0 8px;

        &.shrink {
          width: 1px;
          white-space: nowrap;
        }

        &:nth-child(2) {
          width: 30%;
        }

        &:nth-child(3) {
          width: 30%;
        }

        &:last-child {
          width: 110px;
          white-space: nowrap;
        }
      }
    }
  }
}

.min100 {
  min-width: 100px;
}
</style>
